import React, {
  PropsWithChildren,
  Suspense,
  createContext,
  useContext,
  useEffect,
  useState
} from 'react';
import styled from 'styled-components';
import Loading from '~/purchase-ui/src/components/base/loading';
import FormError from '~/purchase-ui/src/components/base/form-error';
import BaseButton from '~/purchase-ui/src/components/base/button';
import LoadingIcon from '~/purchase-ui/src/components/base/loading-icon';
import SubmitContext from './submit';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import accessToken from '../../../helpers/accessToken';

const ChildrenContainer = styled.div`
  &.hidden {
    display: none;
  }
`;

const defaultState = {
  load: <T,>(_: Promise<T>, _signal?: AbortSignal) => {},
  submit: <T,>(_: Promise<T>, _signal?: AbortSignal) => {},
  error: null as null | Error,
  isSubmitting: false
};

const AppFrameworkContext = createContext(defaultState);

type InitializationCompleteProps = {
  setIsInitializing: (arg0: boolean) => void;
};

const LoadingWrapper: React.FC<InitializationCompleteProps> = ({
  setIsInitializing
}) => {
  useEffect(() => {
    setIsInitializing(true);
    return () => {
      setIsInitializing(false);
    };
  });
  return <Loading />;
};

const AppFrameworkContextProvider: React.FC<PropsWithChildren<{}>> = ({
  children
}) => {
  const { onSubmit } = useContext(SubmitContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(null as null | Error);
  const [error, setError] = useState(null as null | Error);

  const sendEvent = (eventName: string, success: boolean, data: any) => {
    trackEvent(eventName, {
      url: window.location.pathname,
      userId: accessToken?.().user.user_id,
      successEvent: success,
      metadata: data ?? {},
      devicePlatform: 'web'
    });
  };

  const sendLoadEvent = (success: boolean, data: any) => {
    sendEvent('pp_load', success, data);
  };

  const sendSubmitEvent = (success: boolean, data: any) => {
    sendEvent('pp_complete', success, data);
  };

  const load = <T,>(promise: Promise<T>, signal?: AbortSignal) => {
    setIsLoading(true);
    promise
      .then(data => {
        sendLoadEvent(true, data);
        return data;
      })
      .catch(rawLoadError => {
        if (signal?.aborted) {
          return;
        }
        sendLoadEvent(false, rawLoadError);
        setLoadError(rawLoadError);
        throw rawLoadError;
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const submit = <T,>(promise: Promise<T>, signal?: AbortSignal) => {
    setIsSubmitting(true);
    setError(null);
    promise
      .then(value => {
        sendSubmitEvent(true, value);
        onSubmit().finally(() => {
          setIsSubmitting(false);
        });
        return value;
      })
      .catch(submitError => {
        if (signal?.aborted) return;
        sendSubmitEvent(false, submitError);
        setError(submitError);
        setIsSubmitting(false);
        console.log(submitError);
      });
  };

  if (loadError !== null) {
    throw loadError;
  }

  const showLoading = isLoading || isInitializing;

  return (
    <>
      <AppFrameworkContext.Provider
        value={{ load, submit, error, isSubmitting }}
      >
        <Suspense
          fallback={<LoadingWrapper setIsInitializing={setIsInitializing} />}
        >
          {showLoading && <Loading />}
          <ChildrenContainer className={showLoading ? 'hidden' : ''}>
            {children}
          </ChildrenContainer>
        </Suspense>
      </AppFrameworkContext.Provider>
    </>
  );
};

type AppFrameworkButtonProps = {
  isPrimaryCta?: boolean;
};

const ButtonErrorWrapper = styled.div`
  display: inline-block;
`;

const Button: React.FC<
  React.ComponentProps<typeof BaseButton> & AppFrameworkButtonProps
> = props => {
  const { isPrimaryCta = false, ...rest } = props;
  const { disabled } = rest;
  const { error, isSubmitting } = useContext(AppFrameworkContext);
  return (
    <ButtonErrorWrapper>
      {isPrimaryCta && error && (
        <FormError>There was an unexpected error.</FormError>
      )}
      {isPrimaryCta && isSubmitting && <LoadingIcon />}
      {(!isPrimaryCta || !isSubmitting) && (
        <BaseButton
          {...rest}
          disabled={(!isPrimaryCta && isSubmitting) || disabled}
        />
      )}
    </ButtonErrorWrapper>
  );
};

export default AppFrameworkContext;
export { AppFrameworkContextProvider, Button };
